'use client';

import * as React from 'react';
import { SurfaceLink } from '@/components/atoms/SurfaceLink';
import { useDiscoverHref } from '@/hooks/useDiscoverHref';
import { usePathname } from 'next/navigation';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/components/atoms/primitives/utils';

export interface BreadcrumbsProps {
  /** Map of path segments to human-readable labels (e.g., { events: "Events" }). */
  labels?: Record<string, string>;
  /**
   * Override the auto-generated breadcrumb trail with a custom set of crumbs.
   * The trailing (current-page) crumb may omit `href` — it renders as an
   * `aria-current="page"` span rather than a link, so callers must never pass a
   * placeholder `href="#"` dead link for it.
   */
  overrides?: Array<{ href?: string; label: string; discoverQuery?: string }>;
  /**
   * Visual treatment. `'default'` is the neutral sentence-case trail with a
   * chevron separator. `'mono-caps'` matches the `.cat-crumbs` design-system
   * style — monospace, sentence case, a ChevronRight separator, and a
   * skyline-glow current leaf — used in the category hub hero.
   */
  variant?: 'default' | 'mono-caps';
  /** Additional CSS class names. */
  className?: string;
}

interface BreadcrumbItem {
  /** Omitted on the trailing current-page crumb, which renders as a span. */
  href?: string;
  label: string;
  /**
   * WEB-1231 / R25: marks a crumb that points INTO Discover. Its href is
   * resolved by `useDiscoverHref` (signed in → `app.*`) and wins over `href`.
   */
  discoverQuery?: string;
}

function capitalize(str: string): string {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function buildCrumbsFromPathname(
  pathname: string,
  labels: Record<string, string>,
): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: BreadcrumbItem[] = [{ href: '/', label: labels['home'] ?? 'Home' }];

  segments.forEach((segment, index) => {
    const href = '/' + segments.slice(0, index + 1).join('/');
    const label = labels[segment] ?? capitalize(segment.replace(/-/g, ' '));
    crumbs.push({ href, label });
  });

  return crumbs;
}

export function Breadcrumbs({
  labels = {},
  overrides,
  variant = 'default',
  className,
}: BreadcrumbsProps): React.ReactElement | null {
  const pathname = usePathname();
  const toDiscover = useDiscoverHref();

  const crumbs: BreadcrumbItem[] = (overrides ?? buildCrumbsFromPathname(pathname, labels)).map(
    (crumb) =>
      crumb.discoverQuery === undefined
        ? crumb
        : { ...crumb, href: toDiscover(crumb.discoverQuery) },
  );

  const isMonoCaps = variant === 'mono-caps';
  const navClass = isMonoCaps ? 'text-micro tracking-[0.08em] font-mono' : 'text-sm';
  const linkClass = isMonoCaps
    ? 'inline-flex min-h-11 min-w-11 desktop:min-h-0 desktop:min-w-0 items-center justify-center text-muted-foreground hover:text-accent-text transition-colors'
    : 'inline-flex min-h-11 min-w-11 desktop:min-h-0 desktop:min-w-0 items-center justify-center text-muted-foreground hover:text-foreground transition-colors';
  const currentClass = isMonoCaps
    ? 'inline-flex min-h-11 desktop:min-h-0 items-center font-medium text-clt-skyline-glow'
    : 'inline-flex min-h-11 desktop:min-h-0 items-center text-muted-foreground font-medium';

  if (crumbs.length <= 1) {
    return (
      <nav aria-label="Breadcrumb" className={cn(navClass, className)}>
        <ol className="flex items-center gap-1.5 -my-3">
          <li>
            <span className={currentClass} aria-current="page">
              {crumbs[0]?.label ?? 'Home'}
            </span>
          </li>
        </ol>
      </nav>
    );
  }

  // WEB-319: each interactive crumb gets a ≥44×44 hit area (`min-h-11 min-w-11`)
  // wrapped in negative vertical margin on the `<ol>` so the visible breadcrumb
  // row height stays unchanged at 390/1440. The current-page `<span>` is not
  // a tap target but we still apply `min-h-11` so it baselines with its
  // neighbours in the flex row.
  return (
    <nav aria-label="Breadcrumb" className={cn(navClass, className)}>
      <ol className="flex items-center gap-1.5 -my-3">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;

          return (
            <li key={crumb.href ?? crumb.label} className="flex items-center gap-1.5">
              {index > 0 ? (
                <ChevronRight
                  className="inline size-[1em] shrink-0 text-muted-foreground/60"
                  aria-hidden="true"
                />
              ) : null}
              {/* Render as the current-page span when this is the trailing
                  crumb OR it carries no `href` — never an `href="#"` dead link. */}
              {isLast || crumb.href == null ? (
                <span className={currentClass} aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <SurfaceLink href={crumb.href} className={linkClass}>
                  {crumb.label}
                </SurfaceLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
